import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import Button from '@/components/ui/Button';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import {
  UNLIMITED_PAYMENTS,
  formatDate,
  formatMoney,
  formatRowMoney,
  getPaymentSourceLabel,
  isUnlimited,
  parseDate,
} from '../standingOrderModel';
import { StandingOrderActionResult, actionUrl, describeActionError } from '../standingOrderActions';
import {
  BankEditForm,
  UpdateBankStandingOrderRequest,
  hasBankChanges,
  hasBankErrors,
  initialBankEditForm,
  planBankEdit,
} from '../bankStandingOrderActions';
import chargeStyles from '../AdditionalInfo/StandingOrderChargeDialog.module.css';
import styles from './OrderActions.module.css';

interface EditBankOrderDialogProps {
  order: NedarimStandingOrderDtoIn;
  fundId: number;
  onClose: () => void;
  onDone: (kind: 'edit', result: StandingOrderActionResult, request: UpdateBankStandingOrderRequest) => void;
}

/**
 * Editing a bank standing order the office keeps in APT (HAT-1441): the monthly amount, down only,
 * and the payments still to come. Saved straight into the order's APT row.
 *
 * Not the billing day: moved inside a month the bank has already collected in, it could collect a
 * second time that month, and how APT decides that is the office's to confirm first. Not the
 * account either: a new account is a new mandate the donor signs.
 *
 * The same form and the same words as the credit edit (Chaya, 19.10.2026), without what a bank order
 * does not have here: a next date to pick, and a note at Nedarim.
 */
const EditBankOrderDialog = ({ order, fundId, onClose, onDone }: EditBankOrderDialogProps) => {
  const { t, i18n } = useTranslation();
  const prefix = 'standingOrders.orderActions.editDialog';
  const bankPrefix = 'standingOrders.orderActions.bank.editDialog';
  const [form, setForm] = useState<BankEditForm>(() => initialBankEditForm(order));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const plan = useMemo(() => planBankEdit(order, form), [order, form]);

  const money = (value: number) => formatMoney(value, 'ILS', i18n.language);
  const day = (date: Date) => formatDate(date, i18n.language);

  const { mutate, isPending } = usePostAuthApi<StandingOrderActionResult, UpdateBankStandingOrderRequest>(
    actionUrl(fundId, order, 'edit'),
    {
      onSuccess: (result, request) => {
        setSaved(true);
        onDone('edit', result, request);
      },
      onError: (e) => setError(describeActionError(e, t, true)),
    },
  );

  const update = (patch: Partial<BankEditForm>) => {
    setError(null);
    setForm((current) => ({ ...current, ...patch }));
  };

  const name = order.clientName || t('standingOrders.donor');
  const source = getPaymentSourceLabel(order);
  const next = parseDate(order.nextDate);
  const remaining = order.remainingPayments;
  const paymentsNow = remaining == null || isUnlimited(remaining)
    ? t('standingOrders.unlimited')
    : remaining > 0
      ? t(`${prefix}.paymentsLeft`, { count: remaining })
      : t(`${prefix}.noPaymentsLeft`);

  const summary: string[] = [];
  if (plan.changes.amount) {
    summary.push(next
      ? t(`${prefix}.summaryAmount`, { from: money(plan.changes.amount.from), to: money(plan.changes.amount.to), date: day(next) })
      : t(`${prefix}.summaryAmountNoDate`, { from: money(plan.changes.amount.from), to: money(plan.changes.amount.to) }));
  }
  if (plan.changes.payments) {
    summary.push(plan.changes.payments.to >= UNLIMITED_PAYMENTS
      ? t(`${prefix}.summaryUnlimited`)
      : t(`${prefix}.summaryPayments`, { count: plan.changes.payments.to }));
  }

  const fieldError = (field: keyof typeof plan.errors) => {
    const code = plan.errors[field];
    if (!code) return null;
    const key = code === 'paymentsTooMany' ? `${bankPrefix}.errors.${code}` : `${prefix}.errors.${code}`;
    return <span className={styles.fieldError}>{t(key)}</span>;
  };

  const canSave = hasBankChanges(plan) && !hasBankErrors(plan) && !isPending;

  return (
    <MainDialogLayout
      open
      onOpenChange={(open: boolean) => { if (!open && !isPending) onClose(); }}
      title={t(`${prefix}.title`, { name })}
      maxWidth="680px"
      fitContent
      contentClassName={chargeStyles.compactDialog}
    >
      <div className={chargeStyles.container}>
        <div className={chargeStyles.form}>
          <div className={chargeStyles.context}>
            <span>{[name, source && t('standingOrders.orderActions.bank.cancelDialog.account', { source })].filter(Boolean).join(' · ')}</span>
            <b>
              {next
                ? t(`${prefix}.current`, { amount: formatRowMoney(order.amount, order, i18n.language), payments: paymentsNow, date: day(next) })
                : t(`${prefix}.currentNoDate`, { amount: formatRowMoney(order.amount, order, i18n.language), payments: paymentsNow })}
            </b>
          </div>

          <div className={chargeStyles.fieldRow}>
            <div className={chargeStyles.field}>
              <label className={chargeStyles.label} htmlFor="edit-bank-order-amount">
                {t(`${prefix}.amount`)}<span className={chargeStyles.required}>*</span>
              </label>
              <div className={chargeStyles.inputWrapper}>
                <span className={chargeStyles.currencyBadge}>₪</span>
                <input
                  id="edit-bank-order-amount"
                  className={chargeStyles.input}
                  type="number"
                  min="1"
                  max={order.amount}
                  step="any"
                  value={form.amount}
                  onChange={(e) => update({ amount: e.target.value })}
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  disabled={isPending || saved}
                  aria-invalid={Boolean(plan.errors.amount)}
                />
              </div>
              {fieldError('amount') ?? <span className={styles.hint}>{t(`${prefix}.amountHint`)}</span>}
            </div>

            <div className={chargeStyles.field}>
              <label className={chargeStyles.label} htmlFor="edit-bank-order-payments">{t(`${prefix}.payments`)}</label>
              <input
                id="edit-bank-order-payments"
                className={`${chargeStyles.input} ${form.unlimited ? styles.inputLocked : ''}`}
                type="number"
                min="1"
                step="1"
                value={form.unlimited ? '' : form.remainingPayments}
                placeholder={form.unlimited ? t('standingOrders.unlimited') : ''}
                onChange={(e) => update({ remainingPayments: e.target.value })}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                disabled={form.unlimited || isPending || saved}
                aria-invalid={Boolean(plan.errors.payments)}
              />
              <label className={styles.unlimited} htmlFor="edit-bank-order-unlimited">
                <input
                  id="edit-bank-order-unlimited"
                  type="checkbox"
                  checked={form.unlimited}
                  onChange={(e) => update({ unlimited: e.target.checked })}
                  disabled={isPending || saved}
                />
                {t('standingOrders.unlimited')}
              </label>
              {fieldError('payments')}
            </div>
          </div>

          {order.billingDay && (
            <span className={styles.hint}>{t(`${bankPrefix}.billingDayNote`, { day: order.billingDay })}</span>
          )}

          {summary.length > 0 && !saved && (
            <div className={styles.summary} aria-live="polite">
              {summary.map((line) => <span key={line}>{line}</span>)}
            </div>
          )}

          {error && <div role="alert" className={chargeStyles.errorMessage}>{error}</div>}
          {saved && <div role="status" className={chargeStyles.successMessage}>{t(`${bankPrefix}.saved`)}</div>}

          <div className={chargeStyles.actions}>
            {saved ? (
              <Button variant="primary" onClick={onClose}>{t(`${prefix}.close`)}</Button>
            ) : (
              <>
                <Button variant="primary" disabled={!canSave} onClick={() => { setError(null); mutate(plan.request); }}>
                  {isPending ? t(`${prefix}.saving`) : t(`${prefix}.save`)}
                </Button>
                <Button
                  variant="outline"
                  className="!h-[52px] !min-w-[120px] !px-6"
                  onClick={onClose}
                  disabled={isPending}
                >
                  {t(`${prefix}.cancel`)}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default EditBankOrderDialog;
